import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { ADMIN_EMAIL } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<string>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<string> => {
    try {
      setLoading(true);
      const { data: { user, session }, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      if (!user) throw new Error('No user returned from login');

      // Set the user in state
      setUser(user);

      // Get the user type
      const userType = user.user_metadata?.user_type;

      // Handle HOTEL user type
      if (userType === 'hotel') {
        const { data: hotelData, error: hotelSelectError } = await supabase
          .from('hotels')
          .select('id, profile_completed, verification_status')
          .eq('user_id', user.id)
          .maybeSingle();

        if (hotelSelectError && hotelSelectError.code !== 'PGRST116') {
          console.error("Error fetching hotel profile:", hotelSelectError);
          throw hotelSelectError;
        }

        if (!hotelData) {
          // Profile doesn't exist, create one using metadata
          console.log(`Hotel profile not found for user ${user.id}. Creating profile from metadata...`);
          
          const { error: insertError } = await supabase
            .from('hotels')
            .insert([{
              user_id: user.id,
              name: user.user_metadata?.name || user.user_metadata?.property_name || 'New Hotel',
              email: user.email!,
              property_type: user.user_metadata?.property_type || '-',
              address: user.user_metadata?.address || '-',
              city: user.user_metadata?.city || '-',
              country: user.user_metadata?.country || '-',
              star_rating: user.user_metadata?.star_rating || null,
              phone: user.user_metadata?.phone || null,
              verification_status: 'pending_profile',
              profile_completed: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }]);

          if (insertError) {
            console.error("Error creating hotel profile:", insertError);
            
            // If we hit an RLS policy error, try with a more permissive insert
            if (insertError.code === '42501') {
              const { error: fallbackError } = await supabase.auth.updateUser({
                data: { 
                  hotel_profile_needs_creation: true,
                  profile_creation_attempted: new Date().toISOString()
                }
              });
              
              if (fallbackError) {
                console.error("Error updating user metadata:", fallbackError);
              }
            }
            
            // Continue to profile completion regardless of error
            return '/hotel/profile/complete';
          }

          console.log(`Hotel profile created successfully for user ${user.id}.`);
          return '/hotel/profile/complete';
        } else {
          if (!hotelData.profile_completed) {
            return '/hotel/profile/complete';
          }
          
          // Check verification status
          if (hotelData.verification_status === 'pending' || 
              hotelData.verification_status === 'pending_email' ||
              hotelData.verification_status === 'pending_approval') {
            return '/hotel/verification-pending';
          }
          
          return '/hotel/dashboard';
        }
      }

      // Handle AGENT user type
      if (userType === 'agent') {
        const { data: agentData, error: agentSelectError } = await supabase
          .from('agents')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (agentSelectError) {
          console.error("Error fetching agent profile:", agentSelectError);
          throw agentSelectError;
        }

        if (!agentData) {
          console.warn(`Agent profile not found for user ${user.id}. Implement profile creation.`);
          return '/agent/dashboard';
        }

        return '/agent/dashboard';
      }

      // Handle TRAVELER user type
      if (userType === 'traveler') {
        return '/traveler/dashboard';
      }

      // Default redirect
      return '/';
    } catch (error: any) {
      console.error("Login error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}